/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  BasicStep,
  CompanyType,
  ComponentStep,
  DocumentType,
  Field,
  PreconfirmationField,
  ShareholderData,
  CivilStatus,
  PitbullCountries,
  SerializableFile,
  Step,
  OnboardingType,
  PartnerCompanyType,
  StepOnePF,
  StepTwoPF,
  StepThreePF,
  CoownerData,
  StepFourPF,
  MidgardQuestion,
  PersonType,
  CompanyPersonType,
  StepSixPF,
  GetDraftResponse,
  StepSevenPJ,
  StepThreePJ,
  DraftUUID,
  ValidationAttempt,
  StepTwoPJ,
  PitbullCountry,
  Option,
} from "../types";
import { FormikProps, FormikValues } from "formik";
import {
  checkAlreadyRegistered,
  getCountryList,
  getDraft,
  getDrafts,
  getIndividualDocuments,
} from "../connectors/connectors";
import moment from "moment";
import { RenaperData, updateRenaperData } from "../reducers/renaperReducer";
import {
  getParsedRenaperData,
  getQuestions,
  getRenaperInformation,
  goToStep,
  isDev,
  parseBirthDate,
  parseGenderToInitial,
  parseTelephone,
  buildFullAddress,
} from "../app/utils";
import { parseQualifiedInvestorFromDraft } from "../steps/shared/qualifiedInvestorField";
import { deleteDoc, doc } from "firebase/firestore";
import {
  InfoStepType,
  onUpdateInfoStep,
  onUpdatePendingOnboardingType,
} from "../reducers/infoStepReducer";
import { getFirestore } from "firebase/firestore";
import { logAnalyticsAndHotjarEvent } from "../main";
import { AppDispatch, RootState, store } from "../app/store";
import {
  addSendedStep,
  onUpdateDraftUUID,
  onUpdateFormData,
  onUpdateHasExternalInformation,
  onUpdateRemoteIdentityProgress,
  onUpdateResumeStep,
  onUpdateStep,
} from "../reducers/onboardingDataReducer";
import * as Sentry from "@sentry/react";
import { getCompanyData } from "../utils/companies";
import { Theme } from "@allaria-tech/matter/ThemeProvider";
import { Typography } from "@allaria-tech/matter/Typography";
import { firestoreCollections, PERCENTAGE_DECIMALS } from "../constants";
import { getLastTraceId } from "./lastTraceId";
import { ReactNode } from "react";

const staticUrl = import.meta.env.VITE_BASE_URL_STATIC;

export const isAllariaOnboarding = () => {
  const company = import.meta.env.VITE_COMPANY as CompanyType;
  return company === "ALLARIA";
};

export const isAgrofinanzasOnboarding = () => {
  const company = import.meta.env.VITE_COMPANY as CompanyType;
  return company === "AGROFINANZAS";
};
export const isAllariaFondosOnboarding = () => {
  const company = import.meta.env.VITE_COMPANY as CompanyType;
  return company === "ALLARIAFONDOS";
};

export const isAllariaMasOnboarding = () => {
  const company = import.meta.env.VITE_COMPANY as CompanyType;
  return company === "ALLARIAMAS";
};

export const MOBILE_BREAKPOINT = 768;

export const isMobileViewport = () => window.innerWidth < MOBILE_BREAKPOINT;

// The Allaria Digital build only exists to serve the crypto onboarding embedded
// in the Allaria app; it has no generic onboarding of its own.
export const isAllariaDigitalOnboarding = () => {
  const company = import.meta.env.VITE_COMPANY as CompanyType;
  return company === "ALLARIADIGITAL";
};

// Function to check if a Step is a BasicStep
export const isBasicStep = (
  step?: Step | BasicStep | ComponentStep,
): step is BasicStep => {
  // Check if step is defined and does not have a component property
  return step !== undefined && ("fields" in step || "subSteps" in step);
};

// Function to check if a Step is a ComponentStep
export const isComponentStep = (
  step?: Step | BasicStep | ComponentStep,
): step is ComponentStep => {
  // Check if step is defined and has a component property
  return step !== undefined && "component" in step;
};

export const getOnboardingFields = (steps: Step[]) => {
  const getFieldsByStep = (step: Step) => {
    let fields: Field[] = [];

    if (step.fields) {
      fields = step.fields;
    } else if (step.subSteps) {
      fields = step.subSteps.flatMap(
        (subStep: BasicStep) => subStep.fields ?? [],
      );
    }

    return fields;
  };

  const onboardingFields = steps.flatMap((step) => getFieldsByStep(step));
  return onboardingFields;
};

export const getPreconfirmationStepFieldsByName = (fields: Field[]) => {
  const formattedFields = getFormattedPreconfirmationFields(fields);

  const fieldsByName: Record<string, PreconfirmationField> =
    formattedFields.reduce((acc, field) => {
      return { ...acc, [field.name]: field };
    }, {});

  return fieldsByName;
};

export const isUnifiedOnboarding = (): boolean => {
  if (!isDev) return false;
  const params = new URLSearchParams(window.location.search);
  return (
    params.has("accountId") &&
    params.has("accountName") &&
    params.has("accountCuit") &&
    params.has("accountEmail")
  );
};

export const getFormattedPreconfirmationFields = (
  fields: Field[],
  values: FormikValues | null = null,
) => {
  return fields.map(
    ({
      name,
      label,
      type,
      options,
      conditional,
      preconfirmationLabel,
      formatInPreConfirmation,
      valueGetter,
      canUploadMultipleFiles,
    }) => ({
      name,
      label: preconfirmationLabel ?? label,
      type,
      options,
      isConditional: !!conditional,
      formatInPreConfirmation,
      values,
      valueGetter,
      canUploadMultipleFiles,
    }),
  );
};

export const juridicalDocumentLinks = {
  "businessDocumentation.membersNote":
    isAgrofinanzasOnboarding() || isAllariaFondosOnboarding()
      ? `${staticUrl}/docs/agro/4-%2BMODELO%2BENTIDADES-AGRUPACIONES.docx`
      : `${staticUrl}/docs/4-+MODELO+ENTIDADES-AGRUPACIONES.docx`,
  "businessDocumentation.trustNote": `${staticUrl}/docs/2-+MODELO+FIDEICOMISO.docx`,
  "businessDocumentation.shareholdersNote":
    isAgrofinanzasOnboarding() || isAllariaFondosOnboarding()
      ? `${staticUrl}/docs/agro/1-%2BMODELO%2BDDJJ%2BACCIONISTAS%2BY%2BBF.docx`
      : `${staticUrl}/docs/1-+MODELO+DDJJ+ACCIONISTAS+Y+BF.docx`,
  "businessDocumentation.partnersNote": `${staticUrl}/docs/3-+MODELO+SGR.doc`,
};

// Variables for support contact
export const PHONE_NUMBER = getCompanyData().phone;

const getSupportMessage = (supportDataTitle: string) => {
  const { onboardingData, securities } = store.getState();
  const isRemoteStep = ["/dni", "/identidad"].includes(
    window.location.pathname,
  );
  const remoteParams = new URLSearchParams(
    isRemoteStep ? window.location.search : "",
  );
  const remoteEmail = remoteParams.get("email")?.replace(/ /g, "+");

  const draftUuid =
    remoteParams.get("uuid") ||
    onboardingData.draftUUID ||
    securities.draftUUID;
  const email =
    remoteEmail ||
    onboardingData.formValues?.email ||
    securities.initialValidationData.email;
  const traceId = getLastTraceId();

  const supportData = [
    draftUuid && `ID de apertura: ${draftUuid}`,
    traceId && `ID de error: ${traceId}`,
    email && `Email: ${email}`,
  ].filter(Boolean);

  const greeting =
    "Hola, tuve un problema en mi apertura de cuenta y necesito ayuda.";

  return supportData.length
    ? `${greeting}

${supportDataTitle} (no los borres, nos ayudan a resolverlo):
${supportData.join("\n")}`
    : greeting;
};

export const getWhatsappLink = () => {
  const message = getSupportMessage("*Datos para Soporte*");
  return `https://wa.me/${PHONE_NUMBER}?text=${encodeURIComponent(message)}`;
};

export const WhatsappLink = ({ children }: { children: ReactNode }) => (
  <a href={getWhatsappLink()} target="_blank" rel="noreferrer">
    {children}
  </a>
);

// The signatory's own form has no referrer: the partnership is the company's.
export const getOnboardingReferrer = (
  formValues?: FormikValues | null,
): string | undefined =>
  formValues?.referrer ||
  formValues?.steps?.find(
    (step: { "@type": string }) => step["@type"] === "1_JURIDICAL",
  )?.referrer ||
  undefined;

export const getSupportMail = () => {
  const { formValues } = store.getState().onboardingData;
  const referrer = getOnboardingReferrer(formValues);

  return getCompanyData(referrer ? { referrer } : null).mail;
};

export const MAIL_BLACKLIST = `aperturas.soporte@allaria.com.ar`;

export const getMailLink = (to: string = getSupportMail()) => {
  const subject = "Problema en mi apertura de cuenta";
  const body = getSupportMessage("Datos para Soporte");
  return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

export const MailLink = ({
  to,
  children,
}: {
  to?: string;
  children: ReactNode;
}) => <a href={getMailLink(to)}>{children}</a>;

export const customToastifyError = (message: string) => {
  return <div>{message}</div>;
};

// Styles for QR Image
export const qrStyle = (theme?: Theme) => ({
  color: {
    // A theme only carries the tokens it overrides, so fall back to the token
    // in use — QRCode needs a concrete colour, it can't resolve a `var()`.
    dark:
      theme?.primary400 ||
      getComputedStyle(document.documentElement)
        .getPropertyValue("--primary-400")
        .trim() ||
      undefined,
  },
  margin: 0,
  width: 200,
});

export const validDniTypes = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/heic",
  "image/heif",
  "application/pdf",
];

export const validBusinessDocumentationTypes = [
  "application/msword", //.doc
  "application/pdf",
  "image/jpeg",
  "image/png",
];

export const maxFileSize = 10; // MB
export const minFileSize = 0.005; // MB (0.005 MB = 5 KB)

export const asHighlighted = (text: string) => (
  <span style={{ color: "var(--primary-300, #2b58a1)" }}>{text}</span>
);

/**
 * Convert a string value to a CUIT format
 * @param {string} value - The value to be converted to CUIT format
 * @returns {string} - The value in CUIT format
 */
export const asCuit = (value: string) =>
  value.slice(0, 2) + "-" + value.slice(2, 10) + "-" + value.slice(10, 11);

export const asDni = (value?: string | null) => {
  const sanitizedValue = value ?? "";
  // Allow 7 or 8 digits
  let valueWithMask = sanitizedValue.replace(/^0/, "");

  if (sanitizedValue.length >= 2 && sanitizedValue.length <= 7) {
    valueWithMask = `${sanitizedValue.slice(0, 1)}.${sanitizedValue.slice(1, 4)}.${sanitizedValue.slice(
      4,
      7,
    )}`;
  } else if (sanitizedValue.length >= 3 && sanitizedValue.length > 7) {
    valueWithMask = `${sanitizedValue.slice(0, 2)}.${sanitizedValue.slice(2, 5)}.${sanitizedValue.slice(
      5,
      8,
    )}`;
  }

  if (valueWithMask.endsWith(".")) {
    return valueWithMask.slice(0, -1);
  } else {
    return valueWithMask;
  }
};

export const hasRenaperData = (state: RootState) =>
  !!(state.renaper.cuil || state.renaper.number);

export const getDniFromCuit = (cuit?: string | null) =>
  (cuit ?? "").replace(/\D/g, "").slice(2, 10);

export const normalizeCuit = (cuit: string) => Number(cuit.replace(/\D/g, ""));

export const cuitsMatch = (cuitA: string, cuitB: string) => {
  if (!cuitA || !cuitB) return true;
  const normalizedA = normalizeCuit(cuitA);
  const normalizedB = normalizeCuit(cuitB);

  return normalizedA === 0 || normalizedB === 0 || normalizedA === normalizedB;
};

// Scrolls `element` into view inside its own scroll container, and nothing
// else. `scrollIntoView` aligns it in every scrollable ancestor, and an
// `overflow: hidden` box still scrolls from code, so on mobile it also moved
// the Layout shell and slid the header under the browser bar.
export const scrollElementIntoScroller = (
  element: HTMLElement,
  block: "start" | "center" = "start",
) => {
  let node = element.parentElement;

  while (node && node !== document.body && node !== document.documentElement) {
    const style = getComputedStyle(node);

    // First `auto`/`scroll` ancestor wins even if it does not overflow right
    // now: it is the designated scroll region. Demanding an overflow here let
    // the walk escape outwards when content had just been added.
    if (style.overflowY === "auto" || style.overflowY === "scroll") {
      // Offsets are border-box, scrolling aligns to the padding box.
      const padTop = Number.parseFloat(style.paddingTop) || 0;
      const top =
        element.getBoundingClientRect().top -
        node.getBoundingClientRect().top -
        node.clientTop -
        padTop;
      const offset =
        block === "center" ? (node.clientHeight - element.clientHeight) / 2 : 0;

      node.scrollTo({ top: node.scrollTop + top - offset, behavior: "smooth" });
      return;
    }

    node = node.parentElement;
  }
};

export const scrollToElement = (id: string) => {
  const element = document.getElementById(id);
  element && scrollElementIntoScroller(element);
};

export const cities = [
  "Provincia de Buenos Aires",
  "CABA",
  "Catamarca",
  "Chaco",
  "Chubut",
  "Córdoba",
  "Corrientes",
  "Entre Ríos",
  "Formosa",
  "Jujuy",
  "La Pampa",
  "La Rioja",
  "Mendoza",
  "Misiones",
  "Neuquén",
  "Río Negro",
  "Salta",
  "San Juan",
  "San Luis",
  "Santa Cruz",
  "Santa Fe",
  "Santiago del Estero",
  "Tierra del Fuego",
  "Tucumán",
];

export const cityOptions = () =>
  cities.map((city) => ({ value: city, label: city }));

export const roundPercentage = (value: number | string | undefined | null) => {
  if (value === undefined || value === null || value === "") return value;

  const parsed = Number(value);
  if (Number.isNaN(parsed)) return value;

  return String(Number(parsed.toFixed(PERCENTAGE_DECIMALS)));
};

//Backend not support "Ciudad Autónoma de Buenos Aires", so we parse it to "CABA"
export const parseStateAddress = (state: string) => {
  if (state === "Ciudad Autónoma de Buenos Aires") return "CABA";
  if (
    [
      "provincia de buenos aires",
      "buenos aires",
      "bs as",
      "bs.as.",
      "bs. as.",
    ].includes(state.toLowerCase())
  )
    return "Provincia de Buenos Aires";
  else return state;
};

export const hasJointSignature = (formValues: FormikValues | null) => {
  if (!formValues) return false;

  return formValues?.businessInformation?.hasJointSignature === "SI";
};

export const isCoowner = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const template = searchParams.get("template");
  return template === "COOWNER" || template === "ADDITIONAL_COOWNER";
};

export const isToros = (formValues: FormikValues | null) => {
  if (!formValues) return false;

  return formValues?.referrer === "Toros Capital";
};

export const isWina = (formValues: FormikValues | null) => {
  if (!formValues) return false;

  return formValues?.referrer === "Wina";
};

export const isLandToken = (formValues: FormikValues | null) => {
  if (!formValues) return false;

  return formValues?.referrer === "Landtoken";
};

export const isLaBragadense = (formValues: FormikValues | null) => {
  if (!formValues) return false;

  return formValues?.referrer?.toLowerCase() === "labragadense";
};
export const isMabra = (formValues: FormikValues | null) => {
  if (!formValues) return false;

  return formValues?.referrer === "MABRA";
};

export const isSav3 = (formValues: FormikValues | null) => {
  if (!formValues) return false;

  return Boolean(formValues?.referrer?.toLowerCase().includes("sav3"));
};

export const isIntervalores = (formValues: FormikValues | null) => {
  if (!formValues) return false;

  return Boolean(formValues?.referrer?.toLowerCase().includes("intervalores"));
};

export const isCompanyWithoutShareholders = (corporateType: string) =>
  [
    "Asociación / Cámara / Cooperativa / Fundación / Mutual",
    "Consorcio",
    "Fideicomiso",
    "Organismo y/o Entidad Gubernamental",
    "SGR - Sociedad de Garantía Recíproca",
  ].includes(corporateType);

export const isJuridicalCuit = (cuit: string) => {
  const juridicalPrefixes = ["30", "33"];
  return juridicalPrefixes.some((prefix) => cuit.startsWith(prefix));
};

// Helper function to convert booleans to "SI"/"NO" or keep null
export const convertBoolean = (value: boolean | null): string | null => {
  return value === null ? null : value ? "SI" : "NO";
};

export const convertStringToBoolean = (value: "SI" | "NO"): boolean =>
  value === "SI";

// referredBool only lives in the form, but step 1 requires it: a resume without
// the user's answer (magic link) would land back on step 1. The draft's
// referrer wins over that answer, since the back rejects every step that
// doesn't carry it
export const getResumeReferralValues = (
  draftReferrer: string | null | undefined,
  values?: FormikValues | null,
) => {
  if (draftReferrer) return { referredBool: "SI", referrer: draftReferrer };
  if (values?.referredBool === "SI" || values?.referredBool === "NO")
    return { referredBool: values.referredBool, referrer: values.referrer ?? "" };
  return { referredBool: "NO", referrer: "" };
};

export const getReferrerToSend = (values?: FormikValues | null) =>
  values?.referredBool === "SI" ? values.referrer : undefined;

export const isEmptyShareholder = (shareholder: ShareholderData) => {
  if (!shareholder) return true;

  return shareholder.personType === "PHYSICAL"
    ? !shareholder.name && !shareholder.lastName
    : !shareholder.legalName;
};

const isMissingStreetOrCity = (address?: { street?: string; city?: string }) =>
  !address?.street || !address?.city;

export const isUncompletedShareholder = (shareholder: ShareholderData) => {
  if (!shareholder) return true;

  if (
    shareholder.personType === "PHYSICAL" &&
    isMissingStreetOrCity(shareholder.address)
  )
    return true;

  if (
    shareholder.foreignTaxDetailBool === "SI" &&
    isMissingStreetOrCity(shareholder.foreignTaxDetail?.address)
  )
    return true;

  const isMarried =
    shareholder?.personType === "PHYSICAL" &&
    shareholder.civilStatus === "MARRIED";

  if (isMarried && (!shareholder.spouseName || !shareholder.spouseLastName)) {
    return true;
  }
  return Object.entries(shareholder).some(([key, value]) => {
    if (!value) {
      if (isMarried) {
        // Si está casado, spouseName y spouseLastName también deben considerarse incompletos si están vacíos
        return key !== "pepDetails" && key !== "spouseCuil";
      } else {
        return (
          key !== "spouseName" &&
          key !== "spouseLastName" &&
          key !== "spouseCuil" &&
          key !== "pepDetails"
        );
      }
    }
    return false;
  });
};

export const minutesToMiliseconds = (minutes: number) => {
  return minutes * 60 * 1000;
};

// Mask email 3 last characters after the @
export const maskEmail = (email: string) => {
  const [user, domain] = email.split("@");
  return `${user.slice(0, -3)}***@${domain}`;
};

export const getCountries = async (): Promise<PitbullCountries> => {
  try {
    const { data } = await getCountryList();
    return data;
  } catch (error) {
    throw new Error("Error al obtener los países");
  }
};

// Most selected countries go first in country and citizenship selects,
// the rest are sorted alphabetically by the displayed label
const PRIORITY_COUNTRIES = ["AR", "UY", "BR", "US"];

const getPriorityIndex = ({ isoAlpha2 }: PitbullCountry) => {
  const index = PRIORITY_COUNTRIES.indexOf(isoAlpha2);
  return index === -1 ? PRIORITY_COUNTRIES.length : index;
};

const sortByPriorityCountries = (
  countries: PitbullCountry[],
  labelKey: "name" | "citizenship",
) =>
  [...countries].sort(
    (c1, c2) =>
      getPriorityIndex(c1) - getPriorityIndex(c2) ||
      c1[labelKey].localeCompare(c2[labelKey], "es", { sensitivity: "base" }),
  );

export const getCountriesOptions = (
  countries: PitbullCountry[] = [],
): Option[] =>
  sortByPriorityCountries(countries, "name").map(({ name }) => ({
    value: name,
    label: name,
  }));

export const getCitizenshipOptions = (
  countries: PitbullCountry[] = [],
): Option[] =>
  [
    ...new Set(
      sortByPriorityCountries(countries, "citizenship").map(
        ({ citizenship }) => citizenship,
      ),
    ),
  ].map((citizenship) => ({ value: citizenship, label: citizenship }));

export const yesOrNoBoolOptions = [
  { value: "SI", label: "Sí" },
  { value: "NO", label: "No" },
];

export const genderOptions = [
  { value: "M", label: "Masculino" },
  { value: "F", label: "Femenino" },
  { value: "X", label: "Otro" },
];

export const pepOptions = [
  { value: "DIRECT", label: "Sí, directamente" },
  { value: "FAMILY", label: "Sí, por vínculo familiar" },
  { value: "NONE", label: "No" },
];

export const civilStatusOptions: { value: CivilStatus; label: string }[] = [
  { value: "SINGLE", label: "Soltero/a" },
  { value: "MARRIED", label: "Casado/a" },
  { value: "WIDOWED", label: "Viudo/a" },
  { value: "DIVORCED", label: "Divorciado/a" },
];

export const employmentStatusOptions = [
  {
    value: "DEPENDENCY_RELATIONSHIP",
    label: "Trabajo en relación de dependencia",
  },
  { value: "MONOTAX", label: "Soy monotributista" },
  { value: "AUTONOMOUS", label: "Soy autónomo/a" },
  { value: "STUDENT", label: "Soy estudiante" },
  { value: "RETIRED", label: "Soy jubilado/a" },
  { value: "UNEMPLOYED", label: "No tengo empleo actualmente" },
];

export const employmentYearsOptions = [
  { value: "Menos de un año", label: "Menos de 1 año" },
  { value: "1 año", label: "1 año" },
  { value: "2 años", label: "2 años" },
  { value: "3 años", label: "3 años" },
  { value: "4 años", label: "4 años" },
  { value: "5 años", label: "5 años" },
  { value: "6 años", label: "6 años" },
  { value: "7 años", label: "7 años" },
  { value: "8 años", label: "8 años" },
  { value: "9 años", label: "9 años" },
  { value: "Más de 10 años", label: "10 años o más" },
];

export const documentTypesOptions = [
  { value: "DNI", label: "Documento Nacional de Identidad" },
  { value: "PASSPORT", label: "Pasaporte" },
];

export const validProofOfAddressTypes = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/pdf",
];

export const validDocumentImageTypes = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/pdf",
  "image/heic",
  "image/heif",
];

export const handleDateInputKeyDown = (
  e: React.KeyboardEvent<HTMLInputElement>,
  value?: string,
) => {
  // Allow: backspace, delete, tab, escape, enter, shift and backslash

  if (
    [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "Shift",
      "/",
    ].includes(e.key)
  ) {
    return true;
  }

  // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
  if (
    (e.ctrlKey || e.metaKey) &&
    ["a", "c", "v", "x"].includes(e.key.toLowerCase())
  ) {
    return true;
  }

  // Only allow numbers
  if (!/^[0-9]$/.test(e.key)) {
    e.preventDefault();
    return false;
  }

  // Prevent more than 10 characters (DD/MM/YYYY format)
  if (value && value.toString().length >= 10) {
    e.preventDefault();
    return false;
  }

  return true;
};

export const handleDateFieldValueChanged = (
  newValue: string,
  setFieldValue: (value: string) => void,
) => {
  const date = [...(newValue ?? "").replace(/\//g, "").substring(0, 8)];
  [2, 4].forEach((at, i) => date.length > at && date.splice(at + i, 0, "/"));
  setFieldValue(date.join(""));
};

export const parseDateToSend = (date: string) => {
  /**
   *Parse date from DD/MM/YYYY to MM/DD/YYYY
   */
  const dateFormatted = moment(date, ["DD/MM/YYYY", "YYYY-MM-DD"], true).format(
    "MM/DD/YYYY",
  );
  return dateFormatted;
};

export const serializableFileAsFile = (
  serializableFile: SerializableFile | File,
) =>
  serializableFile instanceof File
    ? serializableFile
    : new File([serializableFile.blob], serializableFile.name, {
        type: serializableFile.type,
      });

export const utilsForLanguage = (language: "es" | "en") => {
  // Generic content to show for toastify error messages
  const genericToastifyError = {
    es: (
      <div>
        Lo sentimos, algo falló inesperadamente. Intentalo nuevamente y si el
        error persiste, contactate con{" "}
        <WhatsappLink>
          <b>Soporte.</b>
        </WhatsappLink>
      </div>
    ),
    en: (
      <div>
        Sorry, something failed unexpectadly. Try again and contact{" "}
        <WhatsappLink>
          <b>Support</b>
        </WhatsappLink>{" "}
        if the issue persists.
      </div>
    ),
  }[language];

  const fileReadError = (fileName: string) =>
    ({
      es: `No pudimos leer el archivo "${fileName}". Volvé a descargarlo y subilo nuevamente.`,
      en: `We couldn't read "${fileName}". Download it again and upload it.`,
    })[language];

  const fileEmptyError = (fileName: string) =>
    ({
      es: `El archivo "${fileName}" está vacío. Subí una versión válida para continuar.`,
      en: `"${fileName}" is empty. Upload a valid file to continue.`,
    })[language];

  const fileDuplicateError = (fileName: string) =>
    ({
      es: `Ya cargaste un archivo llamado "${fileName}".`,
      en: `You already uploaded a file named "${fileName}".`,
    })[language];

  const addressCountryMismatchError = (country: string) =>
    ({
      es: `La dirección elegida no es de ${country}. Elegí una dirección de ese país.`,
      en: `The selected address is not in ${country}. Choose an address in that country.`,
    })[language];

  return {
    genericToastifyError,
    fileReadError,
    fileEmptyError,
    fileDuplicateError,
    addressCountryMismatchError,
  };
};

const normalizeCountryName = (country: string) =>
  country
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

export const isSameCountry = (countryA: string, countryB: string) => {
  const a = normalizeCountryName(countryA);
  const b = normalizeCountryName(countryB);
  return a === b || a.includes(b) || b.includes(a);
};

export const preconfirmationPhone = (value: string) =>
  value ? (value.startsWith("+") ? value : `+${value}`) : "-";

/**
 * Normalises a phone number to the single leading "+" the backend requires.
 * Matter's phone `TextField` keeps the dial code and its "+" inside the value,
 */
export const withPhonePlus = (value?: string): string =>
  !value ? "" : value.startsWith("+") ? value : `+${value}`;

/**
 * Decimal separator for numbers.
 * @param {string} [locale=es-AR] - Locale to interpret numbers as from.
 * @returns {string} - The decimal separator.
 */
export const decimalSeparatorForLocale = (locale = "es-AR") =>
  Number(1.1).toLocaleString(locale).charAt(1);

/**
 * Format a value as currency.
 * @param {string | number} value - The value to be converted to formatted as currency.
 * @param {string} [locale=es-AR] - Locale to interpret numbers as from.
 * @param {boolean} [alwaysShowDecimalPart=false] - Append ",00" to values without decimal parts.
 * @returns {string} - The value formatted as currency.
 */
export const formatAsCurrency = (
  value: string | number,
  locale = "es-AR",
  alwaysShowDecimalPart = false,
) => {
  const [whole, decimal = "00"] = value.toLocaleString(locale).split(".", 2);

  const showDecimal = (!!decimal && decimal !== "00") || alwaysShowDecimalPart;

  if (!whole) return `$${value}`;
  const wholeNumber = Number.parseInt(whole);
  return isNaN(wholeNumber)
    ? value
    : `$${
        wholeNumber.toLocaleString(locale) +
        (showDecimal ? `${decimalSeparatorForLocale(locale)}${decimal}` : "")
      }`;
};

export const firestoreCollectionByDocumentType: Record<DocumentType, string> = {
  DNI_BACK: "dniImages",
  DNI_FRONT: "dniImages",
  PASSPORT_FIRST_PAGE: "passportImages",
  PASSPORT_SECOND_PAGE: "passportImages",
  PROOF_OF_ADDRESS: "proofOfAddressImages",
  SELFIE: "",
  WORTH_STATEMENT: "",
};

export const firestoreDocumentByDocumentType: Record<DocumentType, string> = {
  DNI_BACK: "frontDNI",
  DNI_FRONT: "backDNI",
  PASSPORT_FIRST_PAGE: "firstPagePassport",
  PASSPORT_SECOND_PAGE: "secondPagePassport",
  PROOF_OF_ADDRESS: "proofOfAddress",
  SELFIE: "",
  WORTH_STATEMENT: "",
};

export const mimeForExtension: Record<string, string> = {
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
  ".pdf": "application/pdf",
  ".heic": "image/heic",
  ".heif": "image/heif",
};

export const emptyNamedSerializableFile = (name: string) => ({
  name: name,
  // HACK: It doesn't really matter, but trying to infer type is cleaner than
  // adding a `""` type to the inputs' valid types
  type:
    mimeForExtension?.[name?.split(".").pop()?.toLowerCase() ?? ""] ??
    "image/jpeg",
  lastModified: 0,
  size: 0,
  blob: new ArrayBuffer(0),
});

export const isEmptyFile = (file: File | SerializableFile) => file.size === 0;

export const { genericToastifyError } = utilsForLanguage("es");

export const getUnificationParams = () => {
  const params = new URLSearchParams(window.location.search);
  const accountId = params.get("accountId") || "";
  const accountName = params.get("accountName") || "";
  const cuit = params.get("accountCuit") || "";
  const email = params.get("accountEmail") || "";

  return { accountId, accountName, cuit, email };
};
export const isPhysicalOnboarding = (onboardingType: OnboardingType) => {
  return onboardingType === "PHYSICAL";
};

export const isJuridicalOnboarding = (onboardingType: OnboardingType) => {
  return onboardingType === "JURIDICAL";
};

export const isSignatoryOnboarding = (onboardingType: OnboardingType) => {
  return onboardingType === "SIGNATORY";
};

/**
 * Midgard only knows PHYSICAL and JURIDICAL, but OnboardingType carries four
 * more variants, so casting one into the other answers 400. The signatory maps
 * to JURIDICAL because what gets checked is the company whose draft they are
 * completing, not the signatory as an individual.
 */
export const getPersonType = (onboardingType: OnboardingType): PersonType =>
  isJuridicalOnboarding(onboardingType) || isSignatoryOnboarding(onboardingType)
    ? "JURIDICAL"
    : "PHYSICAL";

/**
 * The representative types the company CUIT into a form field, so it sits in
 * formValues. The signatory never types it: their template reads the company
 * off the juridical draft steps, and formValues.businessInformation stays
 * empty for them.
 */
export const getCompanyCuit = (formValues?: FormikValues | null) =>
  formValues?.businessInformation?.cuit ??
  formValues?.steps?.find((step: StepTwoPJ) => step["@type"] === "2_JURIDICAL")
    ?.businessInformation?.cuit;

export const getPartnerCompanyType = (
  formValues: FormikValues,
): PartnerCompanyType | null => {
  if (isLandToken(formValues)) {
    return "LANDTOKEN";
  } else if (isLaBragadense(formValues)) {
    return "LA_BRAGADENSE";
  } else if (isSav3(formValues)) {
    return "SAV3";
  } else if (isIntervalores(formValues)) {
    return "INTERVALORES";
  }

  return null;
};

export const loadFavicon = (favicon: string) => {
  const newLink = document.createElement("link");
  newLink.rel = "icon";
  newLink.type = "image/png";
  newLink.href = favicon;
  document.head.appendChild(newLink);
};

export const checkIsEmailAvailable = async ({
  dispatch,
  idNumber,
}: {
  dispatch: AppDispatch;
  idNumber: string;
}) => {
  try {
    const { data } = await checkAlreadyRegistered({
      idNumber,
    });
    if (data.state === "IN_USE") {
      dispatch(onUpdateInfoStep("REGISTERED_EMAIL"));
      dispatch(onUpdateStep("information-step"));
      throw new Error("EMAIL_IN_USE");
    }
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

export const getCompanyPersonType = (
  onboardingType: OnboardingType,
): CompanyPersonType => {
  const companyPersonType = getCompanyData().companyName as PersonType;
  const individualOnboardings: OnboardingType[] = [
    "COOWNER",
    "ADDITIONAL_COOWNER",
    "PHYSICAL",
  ];

  if (individualOnboardings.includes(onboardingType!)) {
    return `${companyPersonType}_INDIVIDUAL` as CompanyPersonType;
  } else {
    return `${companyPersonType}_BUSINESS` as CompanyPersonType;
  }
};

export const getInfoStepData = ({
  infoStep,
  onboardingType,
  options,
}: {
  infoStep: InfoStepType;
  onboardingType: OnboardingType;
  options?: {
    pendingOnboardingType?: OnboardingType;
  };
}) => {
  let title = "";
  let subtitle: string | React.ReactNode = "";
  let primaryText = "";
  let secondaryText = "";
  let alertType: "INFO" | "ERROR" = "INFO";

  switch (infoStep) {
    case "PENDING_REGISTRY_LOCAL":
    case "PENDING_REGISTRY_REMOTE":
      if (
        onboardingType === "SIGNATORY" &&
        infoStep === "PENDING_REGISTRY_REMOTE"
      )
        break;
      title = "Tenés un registro sin terminar";
      subtitle = "¿Deseás retomar desde donde lo dejaste?";
      primaryText = "Retomar registro";
      secondaryText = "Empezar de nuevo";
      break;
    case "ACTIVE_ACCOUNT":
      title = "¡Un proceso veloz!";
      subtitle = "Agilizamos la apertura precargando tus datos.";
      primaryText = "Continuar";
      break;
    case "ACTIVE_ACCOUNT_SUMMARY": //ALLARIA FONDOS
      title = "¡Confirmá tus datos y solicitá la apertura!";
      subtitle = (
        <span>
          Ya tenés una cuenta en Allaria. Al clickear <b>Solicitar apertura</b>{" "}
          utilizaremos la información de tu empresa para abrir una cuenta
          cuotapartista.
        </span>
      );
      primaryText = "Solicitar apertura";
      break;
    case "ONBOARDING_IN_PROGRESS":
      {
        const pendingOnboarding = options?.pendingOnboardingType;

        const pendingOnboardingMessage = (
          <p>
            El mail ingresado está asociado a una apertura{" "}
            {pendingOnboarding === "JURIDICAL" ? "empresarial" : "personal"} en
            curso.
          </p>
        );

        const pendingOnboardingJointMessage = (
          <p>
            El mail ingresado está asociado a una apertura en curso. Para
            finalizarla, los{" "}
            {pendingOnboarding === "SIGNATORY" ? "firmantes" : "cotitulares"}{" "}
            deben completar su registro siguiendo las instrucciones del correo
            enviado a cada{" "}
            {pendingOnboarding === "SIGNATORY" ? "firmante" : "cotitular"}.
          </p>
        );

        title = "Apertura en progreso";
        subtitle = (
          <div>
            {pendingOnboarding &&
              (["JURIDICAL", "PHYSICAL"].includes(pendingOnboarding)
                ? pendingOnboardingMessage
                : pendingOnboardingJointMessage)}
            <br />
            <p>
              ¿Deseas cancelar la apertura en curso y comenzar una nueva o
              modificar el mail?
            </p>
          </div>
        );
        primaryText = "Modificar mail";
        secondaryText = "Cancelar apertura";
      }
      break;
    case "REGISTERED_EMAIL":
      title = "Correo electrónico ya registrado";
      subtitle =
        onboardingType && ["SIGNATORY", "COOWNER"].includes(onboardingType)
          ? "El mail ingresado está asociado a otro número de documento. Por favor, verificá el CUIT/CUIL ingresado."
          : "Por favor, verificá el DNI asociado para continuar.";
      primaryText =
        onboardingType && ["SIGNATORY", "COOWNER"].includes(onboardingType)
          ? "Modificar"
          : "Modificar datos";
      break;
    case "BLACKLIST":
    case "INVALID_PARAMS":
      alertType = "ERROR";
      title = "Apertura no disponible";
      subtitle = (
        <Typography
          type="BODY"
          size="S"
          color="var(--typography-body-color-base)"
        >
          Tu solicitud de apertura de cuenta comitente no puede completarse por
          este canal. Si creés que es un error o tenés dudas, escribinos a{" "}
          <MailLink to={MAIL_BLACKLIST}>
            <Typography
              type="BODY"
              size="S"
              tag="span"
              link
              color="var(--typography-body-color-base)"
            >
              {MAIL_BLACKLIST}
            </Typography>
          </MailLink>
        </Typography>
      );
      primaryText = "Contactar soporte";
      if (infoStep === "BLACKLIST") secondaryText = "Volver";
      break;
    case "DRAFT_NOT_FOUND":
      title = "No pudimos retomar tu registro";
      subtitle =
        "El proceso de registro que habías comenzado ya no está disponible. Iniciá un nuevo registro para continuar.";
      primaryText = "Empezar nuevamente";
      break;
    //Error CUIT
    case null:
      if (onboardingType === "JURIDICAL") {
        title = "El CUIT corresponde a una persona física";
        subtitle = "¿Deseás continuar y registrar una cuenta personal?";
      } else {
        title = "El CUIT corresponde a una persona jurídica";
        subtitle = "¿Deseás continuar y registrar una cuenta empresarial?";
      }
      primaryText = "Continuar";
      secondaryText = "Volver";
      break;
    default:
      break;
  }

  return {
    alertType,
    title,
    subtitle,
    primaryText,
    secondaryText,
  };
};

export const deleteDniImages = async ({
  collection = "dniImages",
  uuid,
}: {
  uuid: string;
  collection?: string;
}) => {
  const db = getFirestore();
  const docRef = doc(db, collection, uuid);
  await deleteDoc(docRef);
};

export const deleteLiveness = async ({
  collection = "livenessValidation",
  uuid,
}: {
  uuid: string;
  collection?: string;
}) => {
  const db = getFirestore();
  const docRef = doc(db, collection, uuid);
  await deleteDoc(docRef);
};

export const getEmailValidationRef = (signatureRequestId: string | number) => {
  const db = getFirestore();
  return doc(
    db,
    firestoreCollections.emailValidation,
    String(signatureRequestId),
  );
};

/**
 * Validates a redirect URL against a whitelist of allowed domains.
 * Prevents open redirect vulnerabilities by ensuring only trusted
 * domains can be used as redirect targets.
 */
export const isAllowedRedirectUrl = (
  url: string,
  allowedDomains: string[],
): boolean => {
  if (!url || allowedDomains.length === 0) return false;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  if (parsed.protocol !== "https:") return false;

  const hostname = parsed.hostname.toLowerCase();

  const isAllowed = allowedDomains.some((domain) => {
    const d = domain.toLowerCase();
    return hostname === d || hostname.endsWith(`.${d}`);
  });

  return isAllowed;
};

/**
 * The only place the app navigates to a caller-supplied URL.
 *
 * The whitelist check is repeated immediately before the assignment rather than
 * trusted from the call site: it keeps the guarantee local to the sink, so no
 * future caller can navigate to an unvalidated URL by forgetting to check, and
 * it is what makes the safety legible instead of spread across modules.
 *
 * Returns false when the URL was rejected, so callers can tell the difference
 * between "navigating away" and "did nothing".
 */
export const navigateToAllowedUrl = (
  rawUrl: string,
  allowedDomains: string[],
): boolean => {
  if (!isAllowedRedirectUrl(rawUrl, allowedDomains)) return false;

  let target: URL;
  try {
    target = new URL(rawUrl);
  } catch {
    return false;
  }

  // Re-asserted at the sink. `isAllowedRedirectUrl` already rejects every
  // scheme but https — this is the line that makes a `javascript:` URL
  // unreachable here no matter how the value arrived.
  if (target.protocol !== "https:") return false;

  window.location.assign(target.href);
  return true;
};

export const contactBlacklistSupport = () => {
  const link = document.createElement("a");
  link.href = getMailLink(MAIL_BLACKLIST);
  link.click();
};

export const checkDifferentTypePendingDraft = (
  draft: GetDraftResponse,
): OnboardingType => {
  const { steps, personType } = draft;

  const pathname = window.location.pathname;

  switch (personType) {
    case "JURIDICAL":
      {
        if (pathname.includes("personal")) return personType;

        const signatories = steps.find(
          (step): step is StepThreePJ => step?.["@type"] === "3_JURIDICAL",
        );
        const signatoriesOnboardings = steps.find(
          (step): step is StepSevenPJ => step?.["@type"] === "7_JURIDICAL",
        );

        if (
          signatories &&
          signatoriesOnboardings &&
          signatories.additionalSignatories.length !==
            signatoriesOnboardings.representatives.length
        ) {
          return "SIGNATORY";
        }
      }
      break;
    case "PHYSICAL":
      {
        if (pathname.includes("empresarial")) return personType;

        const additionalCoowners = steps.find(
          (step): step is StepThreePF => step?.["@type"] === "3_PHYSICAL",
        );
        const additionalCoownersOnboardings = steps.find(
          (step): step is StepSixPF => step?.["@type"] === "6_PHYSICAL",
        );

        if (
          additionalCoowners &&
          additionalCoownersOnboardings &&
          additionalCoowners.coOwners.length !==
            additionalCoownersOnboardings.coOwnerSteps.length
        ) {
          return "ADDITIONAL_COOWNER";
        }
      }
      break;
    default:
      break;
  }

  return null;
};

export const isStepOneSent = (state: RootState) =>
  state.onboardingData.sendedSteps.some((step) =>
    ["1_PHYSICAL", "1_JURIDICAL"].includes(step["@type"]),
  ) || !!state.onboardingData.additionalSentSteps?.stepOne;

//Field by field: setValues resolves prev from the last render, so it would
//drop whatever was set earlier in the same tick
export const setFormValues = (
  formik: FormikProps<FormikValues> | undefined,
  values: FormikValues,
) =>
  Object.entries(values).forEach(([field, value]) =>
    formik?.setFieldValue(field, value, false),
  );

export const completePhysicalSteps = async ({
  dispatch,
  formik,
  steps,
  isActiveAccount,
  onSendSteps,
  draftReferrer,
}: {
  dispatch: AppDispatch;
  formik: FormikProps<FormikValues>;
  steps: any;
  //Has information in ESCO
  isActiveAccount?: boolean;
  onSendSteps?: (stepsData: any) => void;
  //The draft's current one, which the back checks the steps against
  draftReferrer?: string | null;
}) => {
  const { stepOne, stepOneParsed } = getStepOne({
    draftReferrer,
    steps,
    isActiveAccount,
    values: formik?.values,
  });
  const { stepTwo, stepTwoParsed } = getStepTwo({ steps, isActiveAccount });
  const { stepThree, stepThreeParsed } = getStepThree({ steps });

  const { stepFour, stepFourParsed } = await getStepFour({ steps });

  const newValues = {
    ...stepOneParsed,
    ...stepTwoParsed,
    ...stepThreeParsed,
    ...stepFourParsed,
  };

  setFormValues(formik, newValues);

  const stepOneToStore = {
    ...stepOne,
    personalInformation: {
      ...stepOne.personalInformation,
      birthDate: stepOne.personalInformation.birthDate,
    },
    referrer: getReferrerToSend(stepOneParsed),
    accountIdToLink: isDev
      ? stepOne.accountIdToLink || getUnificationParams().accountId || null
      : null,
    "@type": "1_PHYSICAL",
  };

  let stepTwoToStore;
  let stepThreeToStore;
  let stepFourToStore;

  dispatch(onUpdateFormData(newValues));

  if (stepTwo) {
    stepTwoToStore = { ...stepTwo, "@type": "2_PHYSICAL" };
  }
  if (stepThree) {
    stepThreeToStore = { ...stepThree, "@type": "3_PHYSICAL" };
  }
  if (stepFour) {
    stepFourToStore = {
      ...stepFour,
      "@type": "4_PHYSICAL",
      qualifiedInvestorType: stepFour.qualifiedInvestorType ?? "NONE",
    };
  }

  // Add sent steps only if it's a draft
  if (!isActiveAccount) {
    if (onSendSteps) {
      const stepsData: Record<string, any> = { stepOne: stepOneToStore };
      if (stepTwoToStore) stepsData.stepTwo = stepTwoToStore;
      if (stepFourToStore) stepsData.stepFour = stepFourToStore;
      onSendSteps(stepsData);
    } else {
      dispatch(addSendedStep(stepOneToStore));
      if (stepTwoToStore) dispatch(addSendedStep(stepTwoToStore));
      if (stepThreeToStore) dispatch(addSendedStep(stepThreeToStore));
      if (stepFourToStore) dispatch(addSendedStep(stepFourToStore));
    }
  }

  return { stepOneParsed, stepOne, stepTwoParsed, stepTwo };
};

const getStepOne = ({
  steps,
  isActiveAccount,
  values,
  draftReferrer,
}: {
  steps: any;
  isActiveAccount?: boolean;
  values?: FormikValues;
  draftReferrer?: string | null;
}) => {
  const stepOne = steps.find(
    (step: StepOnePF) => step["@type"] === "1_PHYSICAL",
  );

  const {
    accountIdToLink,
    personalInformation: {
      birthDate,
      birthCountry,
      citizenship,
      civilStatus,
      cuit,
      gender,
      name,
      pep,
      pepDetails,
      telephone,
      lastName,
      identityDocument,
      addresses,
      spouse,
    },
  } = stepOne;

  const parsePep = (pep: string) => {
    // Esco doesn't give us specific information about pep, so return empty string
    return pep ?? (isActiveAccount ? "" : "NONE");
  };

  const stepOneParsed = {
    birthDate: parseBirthDate(birthDate),
    birthCountry: birthCountry,
    citizenship: citizenship,
    civilStatus: civilStatus,
    cuit: cuit,
    gender: parseGenderToInitial(gender),
    lastName: lastName,
    name: name,
    pep: parsePep(pep),
    pepDetails: pepDetails,
    telephone: parseTelephone(telephone),
    identityDocument: identityDocument?.idNumber || getDniFromCuit(cuit),
    tramitNumber: identityDocument?.procedureId,
    ...getResumeReferralValues(
      isActiveAccount ? null : (draftReferrer ?? stepOne.referrer),
      values,
    ),
    addresses: {},
    spouseName: undefined,
    spouseLastName: undefined,
    spouseCuil: undefined,
    accountIdToLink: isDev
      ? accountIdToLink || getUnificationParams().accountId || null
      : null,
  };

  if (addresses?.[0]?.street && addresses[0].city) {
    stepOneParsed.addresses = {
      zipCode: addresses[0].zipCode,
      state: addresses[0].state,
      municipality: addresses[0].municipality,
      city: addresses[0].city,
      street: addresses[0].street,
      number: addresses[0].number,
      fullAddress: buildFullAddress(
        addresses[0].street,
        addresses[0].number,
        addresses[0].city,
      ),
      floor: addresses[0].floor,
      apartment: addresses[0].apartment,
      country: addresses[0].country,
    };
  }

  if (spouse) {
    stepOneParsed.spouseName = spouse.name;
    stepOneParsed.spouseLastName = spouse.lastName;
    stepOneParsed.spouseCuil = spouse.cuit;
  }

  return { stepOne, stepOneParsed };
};

const getStepTwo = ({
  steps,
  isActiveAccount,
}: {
  steps: any;
  isActiveAccount?: boolean;
}) => {
  const stepTwo = steps.find(
    (step: StepTwoPF) => step["@type"] === "2_PHYSICAL",
  );

  let stepTwoParsed;

  if (stepTwo) {
    const {
      employmentProfile: {
        activities,
        bankAccounts,
        employmentStatus,
        foreignTaxDetail,
        years,
      },
    } = stepTwo;

    //For the moment, the 'activities' coming from ESCO (endpoint getDrafts) are emptied because they are not parsed from the backend
    const parsedActivities =
      isActiveAccount || activities.length === 0 ? "" : activities;

    const foreignTaxDetailParsed = {
      idNumber: foreignTaxDetail?.idNumber,
      country: foreignTaxDetail?.address?.country,
      state: foreignTaxDetail?.address?.state,
      fullAddress: foreignTaxDetail?.address?.street
        ? `${foreignTaxDetail.address.street} ${foreignTaxDetail.address.number ?? ""} ${foreignTaxDetail.address.city ?? ""}`.trim()
        : "",
      street: foreignTaxDetail?.address?.street,
      number: foreignTaxDetail?.address?.number,
      city: foreignTaxDetail?.address?.city,
      municipality: foreignTaxDetail?.address?.city,
      zipCode: foreignTaxDetail?.address?.zipCode,
      floor: foreignTaxDetail?.address?.floor,
      apartment: foreignTaxDetail?.address?.apartment,
    };

    stepTwoParsed = {
      foreignTaxDetailsBool: foreignTaxDetail ? "SI" : "NO",
      bankAccountsBool: bankAccounts && bankAccounts.length > 0 ? "SI" : "NO",
      employmentProfile: {
        activities: parsedActivities,
        bankAccounts: bankAccounts ? bankAccounts : "",
        employmentStatus,
        ...(foreignTaxDetailParsed
          ? { foreignTaxDetails: foreignTaxDetailParsed }
          : {}),
        years,
      },
    };
  }

  return { stepTwo, stepTwoParsed };
};

const getStepThree = ({ steps }: { steps: any }) => {
  const stepOne = steps.find(
    (step: StepOnePF) => step["@type"] === "1_PHYSICAL",
  );
  const stepThree = steps.find(
    (step: StepThreePF) => step["@type"] === "3_PHYSICAL",
  );
  let stepThreeParsed;

  const originalCoowner = {
    name: stepOne?.personalInformation.name,
    lastName: stepOne?.personalInformation.lastName,
    email: stepOne?.personalInformation.email,
    confirmEmail: stepOne?.personalInformation.email,
    idNumber: stepOne?.personalInformation.identityDocument.idNumber,
  };

  if (stepThree) {
    const coowners = stepThree.coOwners?.map((coowner: CoownerData) => ({
      name: coowner.name,
      lastName: coowner.lastName,
      email: coowner.email,
      confirmEmail: coowner.email,
      idNumber: coowner.idNumber,
    }));

    stepThreeParsed = {
      coowners: [
        ...(originalCoowner ? [originalCoowner] : []),
        ...(coowners ?? []),
      ],
    };
  }

  return { stepThree, stepThreeParsed };
};

const getStepFour = async ({ steps }: { steps: any }) => {
  const stepFour = steps.find(
    (step: StepFourPF) => step["@type"] === "4_PHYSICAL",
  );
  let stepFourParsed;

  const questions = await getQuestions();

  const answers: string[] = stepFour?.investmentProfile?.choices || [];

  const investmentProfileParsed = () => {
    const result: Record<string, string> = {};
    questions.forEach((question) => {
      const res = findAnswerAboutQuestion(question);
      if (res) {
        result[question.externalCode] = res.option;
      }
    });
    return result;
  };

  const findAnswerAboutQuestion = (question: MidgardQuestion) =>
    question.choices.find((choice) =>
      answers.find((answer) => answer === choice.externalCode),
    );

  if (stepFour) {
    const qualifiedInvestorParsed = parseQualifiedInvestorFromDraft(
      stepFour.qualifiedInvestorType,
    );

    stepFourParsed = {
      investmentProfile: investmentProfileParsed(),
      ...qualifiedInvestorParsed,
    };
  }

  return { stepFour, stepFourParsed };
};

const getEscoData = async (cuit: string) => {
  let steps = null;
  try {
    const draftsResponse = await getDrafts({
      cuit,
      personType: "PHYSICAL",
    });
    const draftSteps = draftsResponse?.data?.steps;
    if (draftSteps?.length > 0) steps = draftSteps;
  } catch (error) {
    Sentry.captureException(error);
    console.log(error, "Error on getDrafts");
  }
  return steps;
};

const getRemoteOnboardingDraft = async (draftUUID: DraftUUID) => {
  try {
    const draftResponse = await getDraft({
      draftUUID,
    });
    return draftResponse;
  } catch (error) {
    console.log("Error on getDraft", error);
  }
};

const autocompleteEscoData = async (
  dispatch: AppDispatch,
  formik: FormikProps<FormikValues>,
  escoData: any,
) => {
  await completePhysicalSteps({
    dispatch,
    formik,
    steps: escoData,
    isActiveAccount: true,
  });
  dispatch(onUpdateHasExternalInformation(true));
  dispatch(onUpdateInfoStep("ACTIVE_ACCOUNT"));
};

const autocompleteRemoteData = async (
  dispatch: AppDispatch,
  formik: FormikProps<FormikValues>,
  remoteDraft: GetDraftResponse,
) => {
  dispatch(onUpdateInfoStep("PENDING_REGISTRY_REMOTE"));
  await completePhysicalSteps({
    dispatch,
    formik,
    steps: remoteDraft.steps,
    draftReferrer: remoteDraft.referrer,
  });
};

export const syncRemoteDraftSteps = async (
  completeSteps: (
    steps: GetDraftResponse["steps"],
    remoteDraft: GetDraftResponse,
  ) => Promise<unknown>,
) => {
  const { draftUUID } = store.getState().onboardingData;
  if (!draftUUID) return false;

  const remoteDraft: GetDraftResponse | undefined = (
    await getRemoteOnboardingDraft(draftUUID)
  )?.data;
  if (!remoteDraft?.steps?.length || checkDifferentTypePendingDraft(remoteDraft))
    return false;

  await completeSteps(remoteDraft.steps, remoteDraft);
  return true;
};

const hasOkAttempt = (attempts?: ValidationAttempt[]) =>
  !!attempts?.some((attempt) => attempt.status === "OK");

//The DNI step fills these fields when the user continues past it, and a
//resume skips that step
const applyRenaperDataToForm = async (
  dispatch: AppDispatch,
  formik: FormikProps<FormikValues>,
  draftCuit: string | undefined,
  informationParsed: RenaperData,
  addressKey: "addresses" | "representativesAddresses",
) => {
  //Only maps the citizenship and countries: the rest goes in without them
  const countries = await getCountryList()
    .then(({ data }) => data as PitbullCountry[])
    .catch(() => []);

  //formik.values is the render the resume started from: the store already has
  //what was set since (cuit, ESCO's address), and the draft has the cuit even
  //when nothing did
  const { formValues } = store.getState().onboardingData;
  const values = { ...formik.values, ...formValues };
  values.cuit ||= draftCuit;

  const parsed = getParsedRenaperData(
    values,
    store.getState(),
    informationParsed,
    countries,
    addressKey,
  );

  //What RENAPER or the countries didn't bring would erase what ESCO loaded
  const body = withoutUndefined(parsed);
  if (body[addressKey]) body[addressKey] = withoutUndefined(body[addressKey]);

  setFormValues(formik, body);
  dispatch(onUpdateFormData(body));
};

const withoutUndefined = (values: FormikValues) =>
  Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined),
  );

export const loadRemoteIdentityProgress = async (
  dispatch: AppDispatch,
  remoteDraft: GetDraftResponse,
  formik: FormikProps<FormikValues>,
  addressKey: "addresses" | "representativesAddresses" = "addresses",
  email?: string,
) => {
  dispatch(onUpdateRemoteIdentityProgress(null));

  const ownAttempts = (attempts?: ValidationAttempt[]) =>
    email
      ? attempts?.filter(
          (attempt) => attempt.email?.toLowerCase() === email.toLowerCase(),
        )
      : attempts;

  //The DNI scan checks RENAPER on its own and only leaves a dniScanAttempt:
  //renaperAttempts is just the manual form after failed scans
  const renaperOk =
    hasOkAttempt(ownAttempts(remoteDraft.renaperAttempts)) ||
    hasOkAttempt(ownAttempts(remoteDraft.dniScanAttempts));
  const livenessOk = hasOkAttempt(
    ownAttempts(remoteDraft.faceLivenessAttempts),
  );
  if (!renaperOk && !livenessOk) return;

  try {
    const { informationParsed, externalCode } = await getRenaperInformation();

    dispatch(updateRenaperData({ ...informationParsed, externalCode }));
    await applyRenaperDataToForm(
      dispatch,
      formik,
      email ? undefined : remoteDraft.cuit,
      informationParsed,
      addressKey,
    );
    dispatch(onUpdateRemoteIdentityProgress({ renaperOk, livenessOk }));
  } catch (error) {
    Sentry.captureException(error);
  }
};

//Without form steps there is no pending registry to offer, but a draft with
//the identity checks done still has progress the user may want to keep
export const promptRemoteIdentityResume = (dispatch: AppDispatch) => {
  const { infoStep, onboardingData } = store.getState();
  if (infoStep.type || !onboardingData.remoteIdentityProgress) return;
  dispatch(onUpdateInfoStep("PENDING_REGISTRY_REMOTE"));
};

//The form steps can't tell an unfinished identity check apart: its substeps
//have no fields, so the remote resume point would skip it
export const getUnfinishedIdentityResumePoint = (
  dispatch: AppDispatch,
  defaultStep: number,
) => {
  const { remoteIdentityProgress, sendedSteps } =
    store.getState().onboardingData;
  if (!remoteIdentityProgress) return null;
  //A draft with steps resumes from them, as it did before the identity checks
  //were tracked
  if (remoteIdentityProgress.livenessOk || sendedSteps?.length) {
    //Used once, like getIdentityResumePoint does
    dispatch(onUpdateRemoteIdentityProgress(null));
    return null;
  }
  return getIdentityResumePoint(dispatch, defaultStep);
};

export const getIdentityResumePoint = (
  dispatch: AppDispatch,
  defaultStep: number,
): { step: number; substep?: number } => {
  const { onboardingData, activeTemplate } = store.getState();
  const progress = onboardingData.remoteIdentityProgress;
  if (!progress) return { step: defaultStep };
  dispatch(onUpdateRemoteIdentityProgress(null));

  const identityStep = activeTemplate?.steps?.find(
    ({ label }) => label === "identity",
  );
  if (!identityStep) return { step: defaultStep };

  const identityStepNumber = Number(identityStep.step);
  if (progress.livenessOk) return { step: identityStepNumber + 1 };

  const livenessSubStep = identityStep.subSteps?.find(
    ({ label }) => label === "livenessValidation",
  );
  return {
    step: identityStepNumber,
    substep: livenessSubStep ? Number(livenessSubStep.step) : undefined,
  };
};

export const getHeritageDocuments = async (
  draftUUID?: string,
  cuit?: string,
) => {
  if (!draftUUID || !cuit) return;
  const { data } = await getIndividualDocuments({ draftUUID, cuit });
  return data?.filter(({ type }) => type === "WORTH_STATEMENT");
};

export const physicalEmailVerificationAuthorize = async (
  draftUUID: string,
  dispatch: AppDispatch,
  formik: FormikProps<FormikValues>,
  state: RootState,
) => {
  dispatch(onUpdateRemoteIdentityProgress(null));
  dispatch(onUpdateDraftUUID(draftUUID));

  try {
    const res = await getRemoteOnboardingDraft(draftUUID);
    const remoteDraft = res?.data;

    const { formValues } = state.onboardingData;
    const cuit = formik.values.cuit || formValues?.cuit || remoteDraft?.cuit;
    const email =
      formik.values.email || formValues?.email || remoteDraft?.email;

    if (cuit && !formik.values.cuit) {
      formik.setFieldValue("cuit", cuit);
      dispatch(
        onUpdateFormData({ cuit, identityDocument: getDniFromCuit(cuit) }),
      );
    }
    if (email && !formik.values.email) {
      formik.setFieldValue("email", email);
      dispatch(onUpdateFormData({ email }));
    }

    const isOwnDraft = !!remoteDraft && !checkDifferentTypePendingDraft(remoteDraft);
    const loadIdentityProgress = async () => {
      if (remoteDraft && isOwnDraft)
        await loadRemoteIdentityProgress(dispatch, remoteDraft, formik);
    };

    if (remoteDraft && remoteDraft.steps.length > 0) {
      const otherTypeDraft = checkDifferentTypePendingDraft(remoteDraft);
      //Before the draft's steps, which carry what the user edited
      await loadIdentityProgress();

      if (otherTypeDraft) {
        dispatch(onUpdatePendingOnboardingType(otherTypeDraft));
        dispatch(onUpdateInfoStep("ONBOARDING_IN_PROGRESS"));
      } else {
        await autocompleteRemoteData(dispatch, formik, remoteDraft);

        const heritageFiles = await getHeritageDocuments(draftUUID, cuit);
        if (heritageFiles?.length) {
          formik.setFieldValue("heritageFiles", heritageFiles);
          dispatch(onUpdateFormData({ heritageFiles }));
        }
      }
    } else {
      if (cuit) {
        const escoData = await getEscoData(cuit);
        if (escoData) {
          await autocompleteEscoData(dispatch, formik, escoData);
        }
      }
      //After ESCO, as in the DNI step: RENAPER's data replaces ESCO's
      await loadIdentityProgress();
      promptRemoteIdentityResume(dispatch);
    }

    if (remoteDraft?.referrer) {
      const url = new URL(window.location.href);
      url.searchParams.set("ref", remoteDraft.referrer);
      window.history.replaceState(window.history.state, "", url);
    }

    if (remoteDraft?.referrer && isOwnDraft) {
      const referralValues = getResumeReferralValues(
        remoteDraft.referrer,
        formik.values,
      );
      setFormValues(formik, referralValues);
      dispatch(onUpdateFormData(referralValues));
    }
  } catch (error) {
    console.log(error, "Error on authorize");
    Sentry.captureException(error);
  }
};

export const physicalEmailVerificationSuccess = async (
  dispatch: AppDispatch,
  formik: FormikProps<FormikValues>,
  step: number,
) => {
  logAnalyticsAndHotjarEvent("step_mail_completed_pf");

  const { type } = store.getState().infoStep;
  if (type !== null) {
    dispatch(onUpdateResumeStep(step + 1));
    dispatch(onUpdateStep("information-step"));
  } else {
    const resumePoint = getIdentityResumePoint(dispatch, step + 1);
    dispatch(onUpdateResumeStep(resumePoint.step));

    if (formik.values.cuit) {
      dispatch(
        onUpdateFormData({
          identityDocument: getDniFromCuit(formik.values.cuit),
        }),
      );
    }
    goToStep({
      dispatch,
      step: resumePoint.step,
      substep: resumePoint.substep,
    });
  }
};

export const isNextStepCompleted = (
  _: FormikValues,
  nextStepStatus: "completed" | "disabled",
) => nextStepStatus === "completed";
